import { useEffect, useMemo } from "react";
import { useActiveHappyHours, usePromotions } from "@/features/settings/api";
import type { CartItem, CartPromotion } from "@/stores/cart.store";
import type { Promotion } from "@/types";

interface AutoApplyPromotionsParams {
  cartItems: CartItem[];
  subtotal: number;
  cartPromotion: CartPromotion | null;
  discountAmount: number;
  setPromotion: (promotion: CartPromotion, discount: number) => void;
  clearPromotion: () => void;
}

type PromotionCandidate = { promo: Promotion; discount: number };

function isManualCode(promotion: CartPromotion | null): boolean {
  return promotion?.type === "discount_code";
}

function cappedDiscount(promo: Promotion, discount: number): number {
  if (promo.maxDiscount && discount > promo.maxDiscount) {
    return promo.maxDiscount;
  }

  return discount;
}

function happyHourDiscount(promo: Promotion, subtotal: number): number {
  if (promo.minOrderAmount && subtotal < promo.minOrderAmount) {
    return 0;
  }

  const discount =
    promo.discountType === "percentage"
      ? Math.round(subtotal * (promo.discountValue / 100))
      : promo.discountValue;

  return cappedDiscount(promo, discount);
}

function isCompleteBxgy(promo: Promotion): boolean {
  return Boolean(
    promo.productId &&
      promo.freeProductId &&
      promo.buyQuantity &&
      promo.getQuantity,
  );
}

function buyXGetYDiscount(promo: Promotion, cartItems: CartItem[]): number {
  if (!isCompleteBxgy(promo)) {
    return 0;
  }

  const chargeableItems = cartItems.filter((item) => !item.isComped);

  const buyUnits = chargeableItems
    .filter((item) => item.product.id === promo.productId)
    .reduce((units, item) => units + item.quantity, 0);

  const giveawayCandidatePrices = chargeableItems
    .filter((item) => item.product.id === promo.freeProductId)
    .flatMap((item) => Array<number>(item.quantity).fill(item.unitPrice));

  const buyQuantity = promo.buyQuantity!;
  const getQuantity = promo.getQuantity!;

  const bundles =
    promo.productId === promo.freeProductId
      ? Math.floor(buyUnits / (buyQuantity + getQuantity))
      : Math.min(
          Math.floor(buyUnits / buyQuantity),
          Math.floor(giveawayCandidatePrices.length / getQuantity),
        );

  if (bundles === 0) {
    return 0;
  }

  return sumOfCheapest(giveawayCandidatePrices, bundles * getQuantity);
}

function sumOfCheapest(prices: number[], count: number): number {
  return [...prices]
    .sort((a, b) => a - b)
    .slice(0, count)
    .reduce((sum, price) => sum + price, 0);
}

function bestCandidate(
  candidates: PromotionCandidate[],
): PromotionCandidate | null {
  return candidates
    .filter((candidate) => candidate.discount > 0)
    .reduce<PromotionCandidate | null>(
      (best, candidate) =>
        !best || candidate.discount > best.discount ? candidate : best,
      null,
    );
}

export function useAutoApplyPromotions({
  cartItems,
  subtotal,
  cartPromotion,
  discountAmount,
  setPromotion,
  clearPromotion,
}: AutoApplyPromotionsParams) {
  const { data: activeHappyHoursRaw } = useActiveHappyHours();
  const activeHappyHours = useMemo(() => {
    if (Array.isArray(activeHappyHoursRaw)) {
      return activeHappyHoursRaw;
    }

    return activeHappyHoursRaw?.data ?? [];
  }, [activeHappyHoursRaw]);

  const { data: bxgyData } = usePromotions({
    type: "buy_x_get_y",
    isActive: true,
    limit: 100,
  });

  const activeBxgyPromos = useMemo(
    () => (bxgyData?.data ?? []).filter((promo) => promo.isActive),
    [bxgyData],
  );

  useEffect(() => {
    if (isManualCode(cartPromotion)) {
      return;
    }

    const dropAutoApplied = () => {
      if (cartPromotion) {
        clearPromotion();
      }
    };

    if (subtotal === 0) {
      dropAutoApplied();

      return;
    }

    const best = bestCandidate([
      ...activeHappyHours.map((promo) => ({
        promo,
        discount: happyHourDiscount(promo, subtotal),
      })),
      ...activeBxgyPromos.map((promo) => ({
        promo,
        discount: buyXGetYDiscount(promo, cartItems),
      })),
    ]);

    if (!best) {
      dropAutoApplied();

      return;
    }

    const alreadyApplied =
      cartPromotion?.id === best.promo.id && discountAmount === best.discount;

    if (!alreadyApplied) {
      setPromotion(best.promo, best.discount);
    }
  }, [
    activeHappyHours,
    activeBxgyPromos,
    cartItems,
    subtotal,
    cartPromotion,
    discountAmount,
    setPromotion,
    clearPromotion,
  ]);
}
